import { throwIfAborted, type ConvertContext, type Converter } from '../../core/converter';
import { EngineFailure } from '../../core/errors';
import { blobToBytes } from '../../core/blob-bytes';
import { canonicalExtension, buildOutputName } from '../../core/filename';
import { extensionOf } from '../../core/file-detect';
import { documentFidelity } from '../../core/fidelity';
import { LOCAL_CONVERSION_COST } from '../../core/pricing';
import type { ConversionResult, ConverterCapabilities, ConvertOptions, FileMetadata } from '../../core/types';
import { hasUnbalancedFence, looksLikeHtmlResidue, verifyDocumentOutput } from '../../core/verify';
import { unzipSync } from 'fflate';
import { normalizeNewlines } from './text-codec';

export const DOCX_SOURCE_FORMATS = ['docx'];
export const DOCX_TARGET_FORMATS = ['txt', 'md'];

const MIME_BY_FORMAT: Record<string, string> = {
  txt: 'text/plain',
  md: 'text/markdown',
};

export interface MammothResult {
  value: string;
  messages?: Array<{ type?: string; message?: string }>;
}

/** mammoth 的最小接口面（只声明用到的成员；测试注入假实现）。 */
export interface MammothLike {
  extractRawText(input: { arrayBuffer: ArrayBuffer }): Promise<MammothResult>;
  convertToMarkdown(
    input: { arrayBuffer: ArrayBuffer },
    options?: Record<string, unknown>,
  ): Promise<MammothResult>;
  images: {
    imgElement(callback: (image: unknown) => Promise<{ src?: string; alt?: string }>): unknown;
  };
}

export interface DocxConverterDeps {
  loadMammoth?: () => Promise<MammothLike>;
}

async function defaultLoadMammoth(): Promise<MammothLike> {
  // 直接加载 **浏览器构建**：mammoth 的主入口在 Node 构建下只接受 `path`/`buffer`，
  // 而浏览器构建（browserify 单文件）走 `arrayBuffer` 分支，且不依赖 fs。
  // 这样在浏览器、Worker 与 jsdom 测试环境下的行为完全一致。
  const module = (await import('mammoth/mammoth.browser.js')) as unknown as { default?: MammothLike } & MammothLike;
  return module.default ?? module;
}

/** 把已读入的字节转成不共享底层缓冲的 ArrayBuffer（mammoth 需要独立 buffer）。 */
function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  if (bytes.byteOffset === 0 && bytes.byteLength === bytes.buffer.byteLength) {
    return bytes.buffer as ArrayBuffer;
  }
  return bytes.slice().buffer as ArrayBuffer;
}

/**
 * 数一数源文档里有几个表格（`w:tbl`）。
 *
 * 用途只是**如实说明保真度**：mammoth 的 Markdown 通道不保留表格结构，单元格文字会被
 * 拉平成段落。产物本身没错，但用户看到「表格没了」会以为是转换坏了 —— 与其让人自己发现，
 * 不如在保真度报告里说清楚。只解压 `word/document.xml` 一个条目，代价可忽略。
 */
function countSourceTables(bytes: Uint8Array): number {
  try {
    const entries = unzipSync(bytes, { filter: (file) => file.name === 'word/document.xml' });
    const xml = entries['word/document.xml'];
    if (!xml) return 0;
    return (new TextDecoder().decode(xml).match(/<w:tbl[ >]/g) ?? []).length;
  } catch {
    // 探测失败不影响主流程：这只是提示信息
    return 0;
  }
}

/** 图片在纯文本/Markdown 产物里无法承载：统一剔除占位符并计数，用于保真度说明。 */
function stripImages(markdown: string): { text: string; omitted: number } {
  const matches = markdown.match(/!\[[^\]]*\]\([^)]*\)/g);
  return { text: markdown.replace(/!\[[^\]]*\]\([^)]*\)/g, ''), omitted: matches?.length ?? 0 };
}

function collapseBlankLines(text: string): string {
  return text.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n');
}

/**
 * DOCX → TXT / Markdown（纯本地，零上传）。
 *
 * 用 mammoth 读取 OOXML：TXT 走 extractRawText（纯文本），MD 走 convertToMarkdown
 * （标题、列表、强调映射为 Markdown 标记）。图片一律省略并计入保真度说明 ——
 * 纯文本产物无法承载图片，塞入 base64 数据 URI 只会把文件撑大。
 */
export class DocxDocumentConverter implements Converter {
  private readonly loadMammoth: () => Promise<MammothLike>;

  constructor(deps: DocxConverterDeps = {}) {
    this.loadMammoth = deps.loadMammoth ?? defaultLoadMammoth;
  }

  capabilities(): ConverterCapabilities {
    return {
      id: 'document.docx',
      category: 'document',
      sourceFormats: [...DOCX_SOURCE_FORMATS],
      targetFormats: [...DOCX_TARGET_FORMATS],
      local: true,
      cloud: false,
      defaultFidelity: 'L3',
      mode: 'local',
      requiresUpload: false,
      estimatedCost: LOCAL_CONVERSION_COST,
    };
  }

  canConvert(source: FileMetadata, targetExtension: string): boolean {
    const sourceExtension = canonicalExtension(source.type.extension);
    const target = canonicalExtension(targetExtension);
    return DOCX_SOURCE_FORMATS.includes(sourceExtension) && DOCX_TARGET_FORMATS.includes(target);
  }

  async inspect(file: File): Promise<FileMetadata> {
    return {
      name: file.name,
      size: file.size,
      type: {
        extension: extensionOf(file.name),
        mime: file.type || 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        category: 'document',
      },
    };
  }

  async convert(file: File, options: ConvertOptions = {}, context?: ConvertContext): Promise<ConversionResult> {
    const target = canonicalExtension(String(options.targetFormat ?? 'md'));
    if (!DOCX_TARGET_FORMATS.includes(target)) {
      throw new EngineFailure('UNSUPPORTED_CONVERSION', `DOCX 引擎不支持输出 ${target} 格式`);
    }

    throwIfAborted(context);
    context?.onProgress?.({ progress: 20, stage: '解析文档' });
    const bytes = await blobToBytes(file);
    const arrayBuffer = toArrayBuffer(bytes);
    const sourceTables = target === 'md' ? countSourceTables(bytes) : 0;

    let result: MammothResult;
    try {
      const mammoth = await this.loadMammoth();
      result =
        target === 'md'
          ? await mammoth.convertToMarkdown(
              { arrayBuffer },
              // 图片占位后统一剔除：返回空 src，避免 mammoth 把图片内联成 base64 数据 URI。
              { convertImage: mammoth.images.imgElement(() => Promise.resolve({ src: '', alt: '' })) },
            )
          : await mammoth.extractRawText({ arrayBuffer });
    } catch (error) {
      throw new EngineFailure(
        'MALFORMED_FILE',
        `DOCX 解析失败：${error instanceof Error ? error.message : String(error)}`,
      );
    }

    throwIfAborted(context);
    context?.onProgress?.({ progress: 65, stage: '整理文本' });
    const normalized = normalizeNewlines(result.value ?? '');
    const { text, omitted } = target === 'md' ? stripImages(normalized) : { text: normalized, omitted: 0 };
    const output = `${collapseBlankLines(text).trim()}\n`;

    if (!output.trim()) {
      throw new EngineFailure('MALFORMED_FILE', '文档里没有可提取的正文文本');
    }

    throwIfAborted(context);
    context?.onProgress?.({ progress: 85, stage: '校验' });
    // 产物里的尖括号来自文档正文（`<https://…>`、`<占位符>`），不能当作 HTML 残留；
    // 判据必须收窄到「块级标签/闭标签」，否则会把完好产物拦下（见 verify.ts 的说明）。
    const html = target === 'md' ? looksLikeHtmlResidue(output) : { residue: false, sample: undefined };
    const verification = verifyDocumentOutput({
      parsed: output,
      assertions: [
        { label: '产物非空', ok: output.trim().length > 0 },
        { label: '无残留 OOXML 标签', ok: !/<w:|<\/w:/.test(output) },
        {
          label: html.sample ? `无残留 HTML 标签（疑似命中「${html.sample}」）` : '无残留 HTML 标签',
          ok: target === 'md' ? !html.residue : true,
        },
        // 提示性：文档正文里本就可能写着奇数个 ```，不该因此拦下产物
        { label: '无未闭合代码围栏', ok: !hasUnbalancedFence(output), advisory: true },
      ],
    });

    const notes: string[] = [];
    if (omitted > 0) notes.push(`省略了 ${omitted} 处图片（纯文本产物无法承载图片）`);
    if (sourceTables > 0) {
      notes.push(`已丢弃 ${sourceTables} 个表格的表格结构（Markdown 通道不保留表格，仅保留单元格文字）`);
    }
    if (result.messages?.length) notes.push(`解析器提示 ${result.messages.length} 条（多为样式近似映射）`);
    const fidelity = documentFidelity({ sourceExtension: 'docx', targetExtension: target, notes });

    const mime = MIME_BY_FORMAT[target] ?? 'text/plain';
    const fileName = buildOutputName(file.name, target);
    return {
      blob: new Blob([output], { type: `${mime};charset=utf-8` }),
      fileName,
      metadata: {
        name: fileName,
        size: new TextEncoder().encode(output).length,
        type: { extension: target, mime, category: 'document' },
      },
      fidelity,
      verificationPassed: verification.passed,
      verificationDetails: verification.details,
    };
  }
}
